import React, { useEffect, useState } from "react";
import { resetState, userLogin } from "../redux/authSlice";
import { useFormik } from "formik";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import instance from "../apiInstance";
import "./LoginLux.css";
import "../telegramWebApp";
import config from "../config";
import { authCopy } from "./authCopy";
import { mustRegisterAgain } from "../routes/isGateLeadOnly";

const copy = authCopy(config.STORE_LOCALE);

const validateLogin = (values) => {
  const errors = {};
  if (!values.email) {
    errors.email = copy.emailRequired;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = copy.emailInvalid;
  }
  return errors;
};

const LoginLux = () => {
  const [loading, setLoading] = useState(false);
  const [keepLogged, setKeepLogged] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);

  const formik = useFormik({
    initialValues: { email: "" },
    validate: validateLogin,
    onSubmit: async (values) => {
      setLoading(true);
      const email = values.email.trim().toLowerCase();
      try {
        const { data } = await instance.post("login", {
          email,
          keep_logged_in: keepLogged,
        });
        dispatch({ type: userLogin.fulfilled.type, payload: data.access_token });
        const me = await instance.get("/me", {
          headers: { Authorization: `Bearer ${data.access_token}` },
        });
        if (mustRegisterAgain(me.data?.user)) {
          dispatch(resetState());
          toast(copy.needsRegister);
          navigate(`/register?email=${encodeURIComponent(email)}`);
          return;
        }
        navigate("/home");
      } catch (error) {
        const detail = error?.response?.data?.detail;
        if (error?.response?.status === 403 && (detail === "needs_register" || detail?.code === "needs_register")) {
          toast(copy.needsRegister);
          navigate(`/register?email=${encodeURIComponent(email)}`);
          return;
        }
        if (typeof detail === "string" && detail) {
          toast.error(detail);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    instance
      .get("/me")
      .then((res) => {
        if (cancelled) return;
        if (mustRegisterAgain(res.data?.user)) {
          const email = (res.data?.user?.email || "").trim().toLowerCase();
          dispatch(resetState());
          toast(copy.needsRegister);
          navigate(`/register?email=${encodeURIComponent(email)}`, { replace: true });
          return;
        }
        navigate("/home", { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status !== 401) navigate("/home", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate, dispatch]);

  return (
    <div className="loginlux-root">
      <div className="loginlux-card">
        {/* Brand */}
        <div className="loginlux-brand">
          <p className="loginlux-brand-name">
            <strong>Madamy</strong> Closet
          </p>
          <div className="loginlux-line" />
        </div>

        {/* GIF — usando <img> direto para evitar fundo preto do iframe */}
        <div className="loginlux-gif-wrap">
          <img
            src="https://media.giphy.com/media/8ZzPIFi9Dqv5cqJkDE/giphy.gif"
            alt={copy.gifAlt}
            className="loginlux-gif-img"
          />
        </div>

        {/* Heading */}
        <div className="loginlux-heading">
          <h1 className="loginlux-title">
            {copy.titleBefore} <span>{copy.titleAccent}</span>
          </h1>
          <p className="loginlux-tagline">
            {copy.tagline}
          </p>
        </div>

        {/* Form */}
        <form className="loginlux-form" onSubmit={formik.handleSubmit}>
          <div className="loginlux-field">
            <label className="loginlux-label" htmlFor="email">
              {copy.emailLabel}
            </label>
            <input
              id="email"
              className="loginlux-input"
              type="email"
              name="email"
              placeholder={copy.emailPlaceholder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email.toLocaleLowerCase()}
              autoComplete="email"
            />
            {formik.touched.email && formik.errors.email && (
              <p className="loginlux-error">{formik.errors.email}</p>
            )}
          </div>

          <label className="loginlux-keep">
            <span className="loginlux-keep-checkbox-wrap">
              <input
                type="checkbox"
                className="loginlux-keep-input"
                id="keepLogged"
                checked={keepLogged}
                onChange={(e) => setKeepLogged(e.target.checked)}
              />
              <span className="loginlux-keep-box" aria-hidden="true" />
            </span>
            <span className="loginlux-keep-label">{copy.keepLogged}</span>
            <span className="loginlux-keep-hint">
              {keepLogged ? copy.session30 : copy.session1}
            </span>
          </label>

          {loading ? (
            <div className="loginlux-spinner-wrap" role="status">
              <div className="loginlux-spinner" />
              <span className="visually-hidden">{copy.loading}</span>
            </div>
          ) : (
            <button type="submit" className="loginlux-btn" disabled={!formik.isValid || !formik.dirty}>
              {copy.enter}
            </button>
          )}
        </form>

        {/* Register link */}
        <p className="loginlux-register">
          {copy.noAccount}{" "}
          <Link to="/register">{copy.registerHere}</Link>
        </p>

        {/* Footer */}
        <p className="loginlux-footer">
          {copy.footer}
        </p>
      </div>
    </div>
  );
};

export default LoginLux;
